import * as yup from 'yup';
import { isValidStateForCountry, validatePostalCodeFormat, US_TERRITORIES, isUS, isUSTerritory } from '@agentnet/utils';

const requiredFieldMessage = (fieldName: string): string => `${fieldName} is a required field.`;

export const propertyAddressSchema = yup.object().shape({
  address1: yup
    .string()
    .trim()
    .max(100, 'Address 1 cannot exceed 100 characters')
    .when('noStreetAddress', {
      is: false,
      then: (schema) => schema.required(requiredFieldMessage('Address 1')),
      otherwise: (schema) => schema.notRequired(),
    }),
  address2: yup.string().trim().max(100, 'Address 2 cannot exceed 100 characters').notRequired(),

  city: yup.string().trim().max(50, 'City cannot exceed 50 characters').required(requiredFieldMessage('City')),
  usTerritory: yup.string().required(requiredFieldMessage('U.S or U.S. Territory')),
  state: yup
    .string()
    .required(requiredFieldMessage('State or U.S. Territory'))
    .test('valid-state-for-country', function (value) {
      const { usTerritory } = this.parent;
      if (!isValidStateForCountry(value, usTerritory)) {
        const upperCountry = usTerritory?.toUpperCase();
        if (upperCountry === 'US') {
          return this.createError({
            message: 'Invalid US state code. Must be a valid 2-character ISO-3166 state code (e.g., CA, NY)',
          });
        } else if (US_TERRITORIES.includes(upperCountry)) {
          return this.createError({
            message: `For U.S. Territory ${upperCountry}, the State/U.S. Territory field must also be ${upperCountry}`,
          });
        }
      }
      return true;
    }),
  postalCode: yup
    .string()
    .trim()
    .test('max-length', 'Postal Code cannot exceed 9 characters', function (value) {
      if (!value) return true;
      const trimmedValue = typeof value === 'string' ? value.trim() : value;
      if (trimmedValue === '') return true;
      const { usTerritory } = this.parent;
      // US and US Territories allow ZIP+4 format (10 chars with hyphen, e.g., 12345-6789)
      if (isUS(usTerritory) || isUSTerritory(usTerritory)) {
        return trimmedValue.length <= 10;
      }
      return trimmedValue.length <= 9;
    })
    .required(requiredFieldMessage('Zip'))
    .test('format-validation', function (value) {
      if (!value) return true;
      const { usTerritory } = this.parent;
      const error = validatePostalCodeFormat(value, usTerritory);
      if (error) {
        return this.createError({ message: error });
      }
      return true;
    }),

  noStreetAddress: yup.boolean().notRequired(),

  legalDescriptionType: yup.string().required(requiredFieldMessage('Legal Description Type')),

  otherDescription: yup
    .string()
    .trim()
    .when('legalDescriptionType', {
      is: 'Other',
      then: (schema) => schema.required(requiredFieldMessage('Other Description')),
      otherwise: (schema) => schema.notRequired(),
    }),

  legalDescription: yup.string().trim().required(requiredFieldMessage('Legal Description')),
});

export default propertyAddressSchema;
